import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { PageHeader } from '@/modules/common/components/PageHeader';
import { InviteUserDialog } from '@/modules/employees/components/InviteUserDialog';
import { UploadEmployeesDialog } from '@/modules/employees/components/UploadEmployeesDialog';
import {
  EditOutlined,
  EmailOutlined,
  ExpandMore,
  GroupsOutlined,
  List as ListIcon,
  PersonAddOutlined,
  Upload,
} from '@mui/icons-material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Button, ButtonGroup, Menu, MenuItem } from '@mui/material';
import Tab from '@mui/material/Tab';
import { getRouteApi } from '@tanstack/react-router';
import { useState } from 'react';
import { ListEmployeesTab } from '../components/ListEmployeesTab';
import { ListInvitationsTab } from '../components/ListInvitationsTab';
import { ListTeamsTab } from '../components/ListTeamsTab';
import { TeamDialog } from '../components/TeamDialog';
import { EmployeesSearchParams } from '../types';

const routeApi = getRouteApi('/_admin/employees/');

export function ListEmployeesPage() {
  const userContext = useCurrentUserContext();
  const search = routeApi.useSearch() as EmployeesSearchParams;
  const navigate = routeApi.useNavigate();
  const [selectedTab, setSelectedTab] = useState(search.tab || 'employees');
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [isInviteUserDialogOpen, setIsInviteUserDialogOpen] = useState(false);
  const [isUploadEmployeesDialogOpen, setIsUploadEmployeesDialogOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader pageTitle="Employees">
        {selectedTab === 'teams' && (
          <Button
            type="button"
            size="small"
            variant="outlined"
            onClick={() => setIsCreateTeamDialogOpen(true)}
          >
            <div className="flex gap-2 items-center">
              <EditOutlined />
              Create a Team
            </div>
          </Button>
        )}
        <ButtonGroup
          variant="contained"
          disableElevation
          size="small"
          className="ml-2"
        >
          <Button
            startIcon={<PersonAddOutlined />}
            onClick={() => setIsInviteUserDialogOpen(true)}
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Invite members
          </Button>
          <Button
            aria-controls={anchorEl ? 'invite-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? 'true' : undefined}
            onClick={handleMenuOpen}
            variant="outlined"
            style={{
              minWidth: 40,
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <ExpandMore />
          </Button>
        </ButtonGroup>
        <Menu
          id="invite-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ list: { 'aria-labelledby': 'invite-menu' } }}
        >
          <MenuItem
            onClick={() => {
              setIsUploadEmployeesDialogOpen(true);
              handleMenuClose();
            }}
            className="flex gap-2"
          >
            <Upload style={{ marginRight: 8 }} /> Upload CSV file
          </MenuItem>
        </Menu>
      </PageHeader>
      <TabContext value={selectedTab}>
        <TabList
          className="px-8 mt-8"
          onChange={(_event, newValue) => {
            setSelectedTab(newValue as string);
            navigate({
              search: () => ({
                tab: newValue as string,
              }),
            });
          }}
        >
          <Tab
            value="employees"
            label={
              <div className="flex gap-2 items-center">
                <ListIcon />
                Members
              </div>
            }
          />
          <Tab
            value="teams"
            label={
              <div className="flex gap-2 items-center">
                <GroupsOutlined />
                Teams
              </div>
            }
          />
          <Tab
            value="invitations"
            label={
              <div className="flex gap-2 items-center">
                <EmailOutlined />
                Invitations
              </div>
            }
          />
        </TabList>

        <TabPanel
          value="employees"
          className="flex flex-col empty:hidden flex-1 h-full px-0"
        >
          <ListEmployeesTab />
        </TabPanel>
        <TabPanel
          value="teams"
          className="flex-1 pt-8 px-0 flex flex-col empty:hidden"
        >
          <ListTeamsTab />
        </TabPanel>
        <TabPanel
          value="invitations"
          className="flex flex-col empty:hidden flex-1 h-full px-0"
        >
          <ListInvitationsTab />
        </TabPanel>
      </TabContext>
      <TeamDialog
        open={isCreateTeamDialogOpen}
        onClose={() => setIsCreateTeamDialogOpen(false)}
        onSuccess={() => {
          setIsCreateTeamDialogOpen(false);
        }}
      />
      <InviteUserDialog
        open={isInviteUserDialogOpen}
        onClose={() => setIsInviteUserDialogOpen(false)}
        onSuccess={() => {
          setIsInviteUserDialogOpen(false);
        }}
      />
      <UploadEmployeesDialog
        open={isUploadEmployeesDialogOpen}
        onClose={() => setIsUploadEmployeesDialogOpen(false)}
        company={userContext!.employer!._id!}
        onSuccess={() => {
          setIsUploadEmployeesDialogOpen(false);
        }}
      />
    </div>
  );
}
